import styled from 'styled-components';
import {
  Box,
  Flex,
  H2,
  P,
  Typography,
  Pill,
  spacing,
  colors,
} from '@procore/core-react';
import { UnstyledLink } from '@/react/styles';
import { marketplaceColors } from '@/react/styles/constants';

export const CardHeader = styled(Flex)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
`;

export const CardBody = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm}px;
  height: 120px;
`;

export const CardTitle = styled(H2)`
  color: ${colors.black};
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CardTagline = styled(P)`
  color: ${colors.black};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  margin: 0;
  letter-spacing: 0.64px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CardTypography = styled(Typography)`
  display: inline-block;
  color: ${marketplaceColors.concrete50};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.64px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CardFooter = styled(Box)`
  padding-top: ${spacing.lg}px;
  border-top: 1px solid ${marketplaceColors.scale50};
  height: 75px;
  overflow: hidden;
`;

export const FooterDescription = styled(P)`
  color: ${marketplaceColors.concrete50};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.64px;
  margin: 0;
  height: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

export const CardLink = styled(UnstyledLink)`
  display: block;
  width: 100%;
  min-width: 0; /* Allow grid item to shrink below content size */

  &:focus {
    outline: none;
    box-shadow: none;
  }

  & > * {
    box-shadow: none;
    border-radius: 0;
    border: 1px solid ${marketplaceColors.scale50};
    border-top: none; /* stack avoids double top */
  }

  /* close the stack */
  &:first-child > * {
    border-top: 1px solid ${marketplaceColors.scale50};
    border-top-left-radius: ${spacing.xs}px;
    border-top-right-radius: ${spacing.xs}px;
  }

  &:last-child > * {
    border-bottom-left-radius: ${spacing.xs}px;
    border-bottom-right-radius: ${spacing.xs}px;
  }

  /* tablet layout */
  @media (min-width: 768px) {
    &:not(.skip-tablet-layout) {
      & > * {
        border: none;
        border-radius: 0;
        border-right: 1px solid ${marketplaceColors.scale50};
        border-bottom: 1px solid ${marketplaceColors.scale50};
      }

      &:nth-child(-n + 2) > * {
        border-top: 1px solid ${marketplaceColors.scale50};
      }

      &:nth-child(odd) > * {
        border-left: 1px solid ${marketplaceColors.scale50};
      }

      &:first-child > * {
        border-top-right-radius: 0;
        border-top-left-radius: ${spacing.xs}px;
      }

      &:last-child > * {
        border-bottom-right-radius: ${spacing.xs}px;
      }

      &:nth-child(2) > *,
      &:last-child:first-child > * {
        border-top-right-radius: ${spacing.xs}px;
      }

      &:nth-last-child(-n + 2):nth-child(odd) > * {
        border-bottom-left-radius: ${spacing.xs}px;
      }

      &:nth-last-child(-n + 2) > * {
        border-bottom-left-radius: 0;
      }

      &:last-child:nth-child(even) > *,
      &:nth-child(even):nth-last-child(2) > *,
      &:last-child:first-child > * {
        border-bottom-right-radius: ${spacing.xs}px;
      }
    }

    &.skip-tablet-layout {
      /* baseline: match desktop edges; bump specificity with && */
      && > * {
        border-top: 0;
        border-left: 0;
        border-right: 1px solid ${marketplaceColors.scale50};
        border-bottom: 1px solid ${marketplaceColors.scale50};
        border-radius: 0;
        box-shadow: none;
      }

      /* first row (3) → top */
      &&:nth-child(-n + 3) > * {
        border-top: 1px solid ${marketplaceColors.scale50};
      }

      /* first column (3n+1) → left */
      &&:nth-child(3n + 1) > * {
        border-left: 1px solid ${marketplaceColors.scale50};
      }

      /* corners (same as desktop) */
      &&:first-child > * {
        border-top-left-radius: ${spacing.xs}px;
      }

      &&:nth-child(3) > *,
      &&:last-child:nth-child(-n + 3) > * {
        border-top-right-radius: ${spacing.xs}px;
      }

      &&:nth-last-child(-n + 3):nth-child(3n + 1) > * {
        border-bottom-left-radius: ${spacing.xs}px;
      }

      &&:last-child > *,
      &&:nth-child(3n):nth-last-child(2) > *,
      &&:nth-child(3n):nth-last-child(-n + 3) > * {
        border-bottom-right-radius: ${spacing.xs}px;
      }
    }
  }

  /* desktop layout */
  @media (min-width: 1024px) {
    &&:not(.skip-tablet-layout):nth-child(odd):not(:nth-child(3n + 1)) > * {
      border-left: 0;
      border-bottom-left-radius: 0;
    }

    && > * {
      border-top: 0;
      border-left: 0;
      border-right: 1px solid ${marketplaceColors.scale50};
      border-bottom: 1px solid ${marketplaceColors.scale50};
      border-radius: 0;
      box-shadow: none;
    }

    &&:nth-child(-n + 3) > * {
      border-top: 1px solid ${marketplaceColors.scale50};
    }

    &&:nth-child(3n + 1) > * {
      border-left: 1px solid ${marketplaceColors.scale50};
    }

    &&:not(.skip-tablet-layout):nth-child(2) > * {
      border-top-right-radius: 0;
    }

    &&:first-child > * {
      border-top-left-radius: ${spacing.xs}px;
    }

    &&:nth-child(3) > *,
    &&:last-child:nth-child(-n + 3) > * {
      border-top-right-radius: ${spacing.xs}px;
    }

    &&:nth-last-child(-n + 3):nth-child(3n + 1) > * {
      border-bottom-left-radius: ${spacing.xs}px;
    }

    &&:nth-child(even):nth-last-child(2) > * {
      border-bottom-right-radius: 0;
    }

    &&:last-child > *,
    &&:nth-child(3n):nth-last-child(2) > *,
    &&:nth-child(3n):nth-last-child(-n + 3) > * {
      border-bottom-right-radius: ${spacing.xs}px;
    }
  }
`;

export const Card = styled(Box)`
  height: ${({ size }) => (size === 'sm' ? '274px' : '305px')};
  width: 100%;
  transition: all 0.25s ease;

  &:hover,
  &:focus {
    border-color: ${marketplaceColors.scale50};
    background: ${marketplaceColors.stone40};
    outline: none;
  }
`;

export const CardImage = styled.img`
  display: block;
  max-width: 100%;
  object-fit: cover;
  height: ${({ height }) => (height ? `${height}px` : '100%')};
`;

export const SecurityBadgePill = styled(Pill)`
  height: 24px;
  border-radius: 100px;

  svg {
    width: 18px;
    height: 20px;
  }
`;

export const CardImageWrapper = styled(Flex)`
  width: 100%;
  border-radius: ${spacing.xs}px;
  background-color: ${colors.white};
`;

export const CategoryCardDescription = styled(Typography)`
  color: ${marketplaceColors.concrete50};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.64px;
`;
